// Intent: the owner's monthly "Zarobki" Google Sheet as a first-class tab —
// income sources per ADULT household member, quick monthly amounts with notes,
// and the three phone-first charts (12-month bars with average, per-source
// bars, who-contributes pie; children never count as earners).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';


import { BarsWithAverage, SharePie, type BarDatum } from '@/components/money-charts';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { memberNameOf, useHouseholdStore } from '@/store/household-store';
import {
  entryFor,
  incomeDaySuggestionOf,
  lastMonths,
  memberMonthTotalGrOf,
  memberRangeTotalGrOf,
  monthKeyOf,
  monthTotalGrOf,
  overdueSourcesOf,
  useIncomeStore,
} from '@/store/income-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln } from '@/utils/format';
import type { IncomeKind, IncomeSource } from '@/types/domain';

const MONTHS_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const MONTHS_FULL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const KINDS: { key: IncomeKind; labelKey: string }[] = [
  { key: 'ETAT', labelKey: 'income.kindEtat' },
  { key: 'SWIADCZENIE_800', labelKey: 'income.kind800' },
  { key: 'DZIALALNOSC', labelKey: 'income.kindBusiness' },
  { key: 'PROWIZJA', labelKey: 'income.kindCommission' },
  { key: 'INNE', labelKey: 'income.kindOther' },
];

const PIE_COLORS = ['#208AEF', '#E5484D', '#FFC94D', '#7BE3A2', '#9B6DF0', '#4DD4C6'];

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_FULL[m - 1]} ${y}`;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  return monthKeyOf(new Date(y, m - 1 + delta, 1));
}

function parseZl(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

type ChartTab = 'SUMMARY' | 'SOURCES' | 'SHARE';

export default function IncomeScreen() {
  const { t } = useTranslation();
  const household = useHouseholdStore((s) => s.household);
  const sources = useIncomeStore((s) => s.sources);
  const entries = useIncomeStore((s) => s.entries);
  const addSource = useIncomeStore((s) => s.addSource);
  const removeSource = useIncomeStore((s) => s.removeSource);
  const setEntry = useIncomeStore((s) => s.setEntry);
  const updateSource = useIncomeStore((s) => s.updateSource);
  const dismissDaySuggestion = useIncomeStore((s) => s.dismissDaySuggestion);

  const [month, setMonth] = useState(monthKeyOf(new Date()));
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [amountRaw, setAmountRaw] = useState('');
  const [noteRaw, setNoteRaw] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  // add-source form
  const [addingFor, setAddingFor] = useState<string | null>(null); // memberId
  const [srcName, setSrcName] = useState('');
  const [srcKind, setSrcKind] = useState<IncomeKind>('ETAT');
  const [srcDayRaw, setSrcDayRaw] = useState('');

  const [chartTab, setChartTab] = useState<ChartTab>('SUMMARY');
  const [selectedBar, setSelectedBar] = useState<number | null>(null);
  const [chartSourceId, setChartSourceId] = useState<string | null>(null);

  // Adults only — children never count as earners (owner rule).
  const adults = (household?.members ?? []).filter((m) => m.role !== 'CHILD');
  const activeSources = sources.filter((s) => s.active);
  const overdue = overdueSourcesOf(activeSources, entries);
  const months12 = lastMonths(12);
  // Day-change suggestions (A5): "confirm-day drifted from expectedDay for 3
  // months — move the reminder?". One-shot: "Zostaw" dismisses that day forever.
  const daySuggestions = activeSources
    .map((s) => ({ source: s, day: incomeDaySuggestionOf(s, entries) }))
    .filter((x): x is { source: IncomeSource; day: number } => x.day !== null);

  const openEntry = (source: IncomeSource) => {
    const existing = entryFor(entries, source.id, month);
    setEditingSourceId(source.id);
    setAmountRaw(
      existing
        ? String(Math.round(existing.amountGr / 100))
        : source.expectedAmountGr
          ? String(Math.round(source.expectedAmountGr / 100))
          : '',
    );
    setNoteRaw(existing?.note ?? '');
    setConfirmRemoveId(null);
  };

  const saveEntry = async () => {
    if (!editingSourceId) return;
    await setEntry(editingSourceId, month, parseZl(amountRaw), noteRaw.trim() || null);
    setEditingSourceId(null);
    setAmountRaw('');
    setNoteRaw('');
    // (Rating prompt removed here: the old "month complete + >=2 sources" gate
    //  never fired for single-income users and wasn't a real "wow". The rating
    //  ask now lives on a genuinely delightful moment — see rate trigger TODO.)
  };

  const saveSource = async () => {
    if (!addingFor) return;
    const day = parseInt(srcDayRaw, 10);
    await addSource({
      memberId: addingFor,
      name: srcName,
      kind: srcKind,
      expectedDay: Number.isFinite(day) && day >= 1 && day <= 31 ? day : null,
      expectedAmountGr: null,
    });
    setAddingFor(null);
    setSrcName('');
    setSrcKind('ETAT');
    setSrcDayRaw('');
  };

  // chart data
  const summaryBars: BarDatum[] = months12.map((m) => ({
    label: MONTHS_SHORT[Number(m.split('-')[1]) - 1],
    valueGr: monthTotalGrOf(entries, m),
  }));
  const sourceBars: BarDatum[] = months12.map((m) => ({
    label: MONTHS_SHORT[Number(m.split('-')[1]) - 1],
    valueGr: entries
      .filter((e) => e.month === m && (!chartSourceId || e.sourceId === chartSourceId))
      .reduce((s, e) => s + e.amountGr, 0),
  }));
  const earners = adults
    .map((m, i) => ({
      label: m.firstName,
      valueGr: memberRangeTotalGrOf(activeSources, entries, m.id, months12),
      color: PIE_COLORS[i % PIE_COLORS.length],
    }))
    .filter((d) => d.valueGr > 0);

  const selectedMonthKey = selectedBar !== null ? months12[selectedBar] : null;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: 100 }}>
        <AppText variant="headlineLg" color={colors.navy}>
          {t('income.title')}
        </AppText>

        {/* Month pill navigator (Napper-style range pill) */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'center',
            backgroundColor: colors.card,
            borderRadius: radius.full,
            paddingHorizontal: spacing.sm,
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('income.prevMonth')}
            onPress={() => setMonth((m) => shiftMonth(m, -1))}
            style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="chevron-back" size={20} color={colors.navy} />
          </Pressable>
          <AppText variant="labelLg" color={colors.navy} style={{ minWidth: 130, textAlign: 'center' }}>
            {monthLabel(month)}
          </AppText>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('income.nextMonth')}
            onPress={() => setMonth((m) => shiftMonth(m, 1))}
            style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="chevron-forward" size={20} color={colors.navy} />
          </Pressable>
        </View>

        {/* Household total hero */}
        <View style={{ backgroundColor: colors.primary, borderRadius: radius.card, padding: spacing.screen, gap: spacing.base }}>
          <AppText variant="labelLg" color="#DCE9FF">
            {t('income.totalThisMonth')}
          </AppText>
          <AppText variant="headlineLg" color="#FFFFFF" style={{ fontSize: 36, lineHeight: 42 }}>
            {formatPln(monthTotalGrOf(entries, month))}
          </AppText>
        </View>

        {/* Day-change suggestions (A5) — the app proposes, the human clicks. */}
        {daySuggestions.map(({ source, day }) => (
          <Card key={`sugg-${source.id}`} style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="bulb-outline" size={20} color={colors.navy} />
              <AppText variant="bodyLg" style={{ flex: 1 }}>
                {t('income.daySuggestion', { name: source.name, day })}
              </AppText>
            </View>
            <View style={{ flexDirection: 'row', gap: spacing.sm }}>
              <Pressable
                accessibilityRole="button"
                onPress={() => void updateSource(source.id, { expectedDay: day })}
                style={{ backgroundColor: colors.primary, borderRadius: radius.full, paddingVertical: 8, paddingHorizontal: spacing.md }}
              >
                <AppText variant="labelMd" color="#FFFFFF">
                  {t('income.daySuggestionAccept', { day })}
                </AppText>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                onPress={() => void dismissDaySuggestion(source.id, day)}
                style={{ backgroundColor: colors.card, borderRadius: radius.full, paddingVertical: 8, paddingHorizontal: spacing.md }}
              >
                <AppText variant="labelMd" color={colors.navy}>
                  {t('income.daySuggestionKeep')}
                </AppText>
              </Pressable>
            </View>
          </Card>
        ))}

        {/* Overdue prompts */}
        {overdue.length > 0 && (
          <Card style={{ gap: spacing.sm, backgroundColor: colors.warningBg }}>
            <AppText variant="labelLg" color={colors.warning}>
              {t('income.overdueTitle')}
            </AppText>
            {overdue.map((s) => (
              <Pressable
                key={s.id}
                accessibilityRole="button"
                onPress={() => openEntry(s)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 40 }}
              >
                <Ionicons name="help-circle-outline" size={20} color={colors.warning} />
                <AppText variant="bodyLg" style={{ flex: 1 }}>
                  {t('income.overdueRow', { name: s.name, day: s.expectedDay })}
                </AppText>
                <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
              </Pressable>
            ))}
          </Card>
        )}

        {/* Per-adult sections */}
        {adults.map((member) => {
          const memberSources = activeSources.filter((s) => s.memberId === member.id);
          return (
            <View key={member.id} style={{ gap: spacing.sm }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <AppText variant="headlineSm" style={{ flex: 1 }}>
                  {memberNameOf(household, member.id) === 'Ty' ? t('income.you') : member.firstName}
                </AppText>
                <AppText variant="labelLg" color={colors.navy}>
                  {formatPln(memberMonthTotalGrOf(activeSources, entries, member.id, month))}
                </AppText>
              </View>
              <Card style={{ padding: 0 }}>
                {memberSources.length === 0 && addingFor !== member.id && (
                  <AppText variant="bodyMd" style={{ padding: spacing.md }}>
                    {t('income.noSources')}
                  </AppText>
                )}
                {memberSources.map((source, idx) => {
                  const entry = entryFor(entries, source.id, month);
                  const editing = editingSourceId === source.id;
                  return (
                    <View
                      key={source.id}
                      style={{ borderTopWidth: idx === 0 ? 0 : 1, borderTopColor: colors.divider }}
                    >
                      <Pressable
                        accessibilityRole="button"
                        onPress={() => (editing ? setEditingSourceId(null) : openEntry(source))}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, minHeight: 56 }}
                      >
                        <View style={{ flex: 1, gap: 2 }}>
                          <AppText variant="labelLg">{source.name}</AppText>
                          <AppText variant="labelMd">
                            {t(`income.kind${source.kind === 'SWIADCZENIE_800' ? '800' : source.kind === 'ETAT' ? 'Etat' : source.kind === 'DZIALALNOSC' ? 'Business' : source.kind === 'PROWIZJA' ? 'Commission' : 'Other'}`)}
                            {source.expectedDay ? ` · ${source.expectedDay}. ${t('income.dayOfMonth')}` : ''}
                          </AppText>
                        </View>
                        {entry ? (
                          <AppText variant="labelLg" color={colors.navy}>
                            {formatPln(entry.amountGr)}
                          </AppText>
                        ) : (
                          <AppText variant="labelLg" color={colors.primary}>
                            {t('income.addAmount')}
                          </AppText>
                        )}
                      </Pressable>
                      {editing && (
                        <View style={{ padding: spacing.md, paddingTop: 0, gap: spacing.sm }}>
                          <TextField
                            label={t('income.amountLabel', { month: monthLabel(month) })}
                            placeholder="np. 5 700"
                            keyboardType="numeric"
                            value={amountRaw}
                            onChangeText={setAmountRaw}
                          />
                          {/* Owner: remind at income entry that this is local-only
                              — zarobki are the most sensitive number in the app. */}
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                            <Ionicons name="lock-closed-outline" size={14} color={colors.outline} />
                            <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
                              {t('income.privacyNote')}
                            </AppText>
                          </View>
                          <TextField
                            label={t('income.noteLabel')}
                            placeholder={t('income.notePlaceholder')}
                            value={noteRaw}
                            onChangeText={setNoteRaw}
                          />
                          <PrimaryButton label={t('income.save')} onPress={saveEntry} />
                          <Pressable
                            accessibilityRole="button"
                            onPress={() => {
                              if (confirmRemoveId === source.id) {
                                void removeSource(source.id);
                                setEditingSourceId(null);
                                setConfirmRemoveId(null);
                              } else {
                                setConfirmRemoveId(source.id);
                              }
                            }}
                            style={{ minHeight: 40, alignItems: 'center', justifyContent: 'center' }}
                          >
                            <AppText variant="labelMd" color={colors.error}>
                              {confirmRemoveId === source.id ? t('income.removeConfirm') : t('income.removeSource')}
                            </AppText>
                          </Pressable>
                        </View>
                      )}
                    </View>
                  );
                })}
              </Card>

              {addingFor === member.id ? (
                <Card style={{ gap: spacing.sm }}>
                  <TextField
                    label={t('income.srcNameLabel')}
                    placeholder={t('income.srcNamePlaceholder')}
                    value={srcName}
                    onChangeText={setSrcName}
                  />
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
                    {KINDS.map(({ key, labelKey }) => {
                      const active = srcKind === key;
                      return (
                        <Pressable
                          key={key}
                          accessibilityRole="button"
                          onPress={() => setSrcKind(key)}
                          style={{
                            backgroundColor: active ? colors.navy : colors.card,
                            borderRadius: radius.full,
                            paddingHorizontal: spacing.md,
                            paddingVertical: spacing.xs,
                          }}
                        >
                          <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                            {t(labelKey)}
                          </AppText>
                        </Pressable>
                      );
                    })}
                  </View>
                  <TextField
                    label={t('income.srcDayLabel')}
                    placeholder="np. 29"
                    keyboardType="numeric"
                    value={srcDayRaw}
                    onChangeText={(v) => setSrcDayRaw(v.replace(/[^\d]/g, '').slice(0, 2))}
                  />
                  <AppText variant="labelMd" color={colors.textSecondary}>
                    {t('income.srcDayHint')}
                  </AppText>
                  <PrimaryButton label={t('income.srcSave')} disabled={!srcName.trim()} onPress={saveSource} />
                  <PrimaryButton label={t('common.cancel')} variant="secondary" onPress={() => setAddingFor(null)} />
                </Card>
              ) : (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => setAddingFor(member.id)}
                  style={{ minHeight: 40, justifyContent: 'center' }}
                >
                  <AppText variant="labelLg" color={colors.primary}>
                    + {t('income.addSource')}
                  </AppText>
                </Pressable>
              )}
            </View>
          );
        })}

        {/* Charts */}
        <AppText variant="headlineSm">{t('income.chartsTitle')}</AppText>
        <View style={{ flexDirection: 'row', gap: spacing.xs }}>
          {(
            [
              { key: 'SUMMARY', labelKey: 'income.tabSummary' },
              { key: 'SOURCES', labelKey: 'income.tabSources' },
              { key: 'SHARE', labelKey: 'income.tabShare' },
            ] as { key: ChartTab; labelKey: string }[]
          ).map(({ key, labelKey }) => {
            const active = chartTab === key;
            return (
              <Pressable
                key={key}
                accessibilityRole="button"
                onPress={() => setChartTab(key)}
                style={{
                  backgroundColor: active ? colors.navy : colors.card,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.xs,
                  minHeight: 36,
                  justifyContent: 'center',
                }}
              >
                <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                  {t(labelKey)}
                </AppText>
              </Pressable>
            );
          })}
        </View>

        {chartTab === 'SUMMARY' && (
          <Card style={{ gap: spacing.sm }}>
            <BarsWithAverage
              data={summaryBars}
              onBarPress={(i) => setSelectedBar((prev) => (prev === i ? null : i))}
              selectedIndex={selectedBar}
            />
            {selectedMonthKey && (
              <View style={{ gap: spacing.xs, borderTopWidth: 1, borderTopColor: colors.divider, paddingTop: spacing.sm }}>
                <AppText variant="labelLg" color={colors.navy}>
                  {monthLabel(selectedMonthKey)} — {formatPln(monthTotalGrOf(entries, selectedMonthKey))}
                </AppText>
                {entries
                  .filter((e) => e.month === selectedMonthKey)
                  .map((e) => {
                    const src = sources.find((s) => s.id === e.sourceId);
                    return (
                      <View key={e.id} style={{ flexDirection: 'row', gap: spacing.sm }}>
                        <AppText variant="bodyMd" style={{ flex: 1 }}>
                          {src?.name ?? '?'}
                          {e.note ? ` · ${e.note}` : ''}
                        </AppText>
                        <AppText variant="bodyMd" color={colors.navy}>
                          {formatPln(e.amountGr)}
                        </AppText>
                      </View>
                    );
                  })}
              </View>
            )}
          </Card>
        )}

        {chartTab === 'SOURCES' && (
          <Card style={{ gap: spacing.sm }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
              <Pressable
                accessibilityRole="button"
                onPress={() => setChartSourceId(null)}
                style={{
                  backgroundColor: chartSourceId === null ? colors.primaryTile : colors.background,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 4,
                }}
              >
                <AppText variant="labelMd" color={colors.navy}>
                  {t('income.allSources')}
                </AppText>
              </Pressable>
              {activeSources.map((s) => (
                <Pressable
                  key={s.id}
                  accessibilityRole="button"
                  onPress={() => setChartSourceId((prev) => (prev === s.id ? null : s.id))}
                  style={{
                    backgroundColor: chartSourceId === s.id ? colors.primaryTile : colors.background,
                    borderRadius: radius.full,
                    paddingHorizontal: spacing.sm,
                    paddingVertical: 4,
                  }}
                >
                  <AppText variant="labelMd" color={colors.navy}>
                    {s.name}
                  </AppText>
                </Pressable>
              ))}
            </View>
            <BarsWithAverage data={sourceBars} color="#4DA6F0" />
          </Card>
        )}

        {chartTab === 'SHARE' &&
          (earners.length >= 2 ? (
            <Card style={{ gap: spacing.sm, alignItems: 'center' }}>
              <AppText variant="labelLg" color={colors.navy}>
                {t('income.shareTitle')}
              </AppText>
              <SharePie data={earners} />
              <AppText variant="labelMd" color={colors.textSecondary}>
                {t('income.sharePeriod')}
              </AppText>
            </Card>
          ) : (
            <Card>
              <AppText variant="bodyMd">{t('income.shareNeedsTwo')}</AppText>
            </Card>
          ))}
      </ScrollView>
    </SafeAreaView>
  );
}
